import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  getFacility,
  updateFacility,
  type PublicFacility,
} from '../../lib/facilities-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';
import { FacilityForm, type FacilityFormValues } from './form';

export function FacilityEditPage() {
  const t = useT();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [facility, setFacility] = useState<PublicFacility | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const f = await getFacility(id);
        setFacility(f);
      } catch (e) {
        setLoadError(extractApiError(e).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  async function handleSubmit(values: FacilityFormValues) {
    if (!id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await updateFacility(id, {
        name: values.name.trim(),
        type: values.type,
        requires_booking: values.requires_booking,
        slot_minutes: Number(values.slot_minutes),
        requires_approval: values.requires_approval,
        is_active: values.is_active,
        ...(values.description.trim() ? { description: values.description.trim() } : {}),
        ...(values.capacity.trim() ? { capacity: Number(values.capacity) } : {}),
        ...(values.open_time ? { open_time: values.open_time } : {}),
        ...(values.close_time ? { close_time: values.close_time } : {}),
        ...(values.image_url.trim() ? { image_url: values.image_url.trim() } : {}),
      });
      navigate('/facilities');
    } catch (e) {
      setSubmitError(extractApiError(e).message);
      throw e;
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/facilities"
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('fac.backToList')}
      </Button>

      <div>
        <Typography variant="h4" fontWeight={600}>
          {t('fac.editTitle')}
        </Typography>
      </div>

      {loadError ? <Alert severity="error">{loadError}</Alert> : null}
      {submitError ? <Alert severity="error">{submitError}</Alert> : null}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : facility ? (
        <FacilityForm
          initial={facility}
          submitLabel={t('fac.saveChanges')}
          submitting={submitting}
          isEdit
          onSubmit={handleSubmit}
        />
      ) : !loadError ? (
        // Loaded without an error but got no facility: say so rather than show a blank page.
        <Alert severity="warning">{t('fac.notFound')}</Alert>
      ) : null}
    </Stack>
  );
}
